import { getVaultAccess, isCompanyManager, VaultAccess } from '../../../guards/page-access.util';
import { useSelectedCompany } from '../../../hooks/use-selected-company.hook';

/**
 * Which half of the Data Room the person in front of us is looking at.
 *
 * Three states, not a boolean, because there is a third: the session has not answered yet. A
 * boolean has to default to one of the two views, and whichever it picks is wrong for the other
 * half — defaulting to the owner sent a recipient at `/spaces/home` and a 403 before the role
 * arrived. `UNRESOLVED` cannot be mistaken for either, so a caller that forgets it fails to
 * compile rather than quietly drawing the wrong side.
 */
export enum DataRoomViewer {
  /** Admins and Contributors: the Vault, the internal workspaces, the cards and the modals. */
  OWNER = 'owner',
  /** Reached the module only through a room shared with them; their whole view is those rooms. */
  RECIPIENT = 'recipient',
  /** Draw neither view and call neither route. */
  UNRESOLVED = 'unresolved',
}

export const useDataRoomViewer = (): DataRoomViewer => {
  const { isFetched, selectedCompany } = useSelectedCompany();

  if (!isFetched || !selectedCompany) return DataRoomViewer.UNRESOLVED;

  return isCompanyManager(selectedCompany) ? DataRoomViewer.OWNER : DataRoomViewer.RECIPIENT;
};

/**
 * How much of the Vault the person in front of us reaches. `NONE` until the session answers, so a
 * caller that asks the Vault's routes on that guess is refused by its own check, not by a 403.
 *
 * It is not the same question as the viewer: a Stakeholder sees the recipient's half of the Data
 * Room and, beside it, the folders of the Vault that are theirs.
 */
export const useVaultAccess = (): VaultAccess => {
  const { isFetched, selectedCompany } = useSelectedCompany();

  if (!isFetched || !selectedCompany) return VaultAccess.NONE;

  return getVaultAccess(selectedCompany);
};
